/**
 * lib/store/index.ts (Phase 4)
 *
 * Root Zustand store. Phase 4 adds three new slices on top of Phase 0–3:
 *
 *   - invoices    — uploaded + parsed Invoice rows
 *   - feedbacks   — CrewFeedback rows
 *   - transports  — Transport rows (used by Transport View)
 *
 * It also adds the corresponding low-level setters (`addInvoice`,
 * `updateInvoice`, `addFeedback`, `updateFeedback`, `addTransport`,
 * `updateTransport`) so that phase4-actions.ts can mutate cleanly.
 *
 * All earlier slices/actions from Phase 0–3 remain — only additive.
 *
 * Persistence: every mutation writes through to localStorage via the
 * subscribeWithSelector subscription. Hydration on first client render
 * via StoreHydration.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  AccrualEntry,
  AuditLogEntry,
  Booking,
  Broadcast,
  BroadcastResponse,
  ChatMessage,
  ChatThread,
  Rank,
  TenantSettings,
  Crew,
  CrewAssignment,
  CrewFeedback,
  Flight,
  FxRate,
  Hotel,
  InventoryAlert,
  Invoice,
  NotificationOutboxEntry,
  OfficeDuty,
  ProjectionEntry,
  ProjectionRun,
  PushNotification,
  RfpRequest,
  RosterEvent,
  RosterLine,
  RosterPublication,
  Station,
  SurfaceTravel,
  SystemConfig,
  Branding,
  Training,
  Transport,
  User,
  UserRole,
} from '@/lib/types';
import { loadFromLocalStorage, saveToLocalStorage, clearLocalStorage, PERSIST_ALLOWLIST } from './persistence';
import { loadSeed, type SeedBundle } from './seed';
import type { TimeMode } from '@/lib/utils/date';
// =============================================================================
// Vendor mappings — anchor for role-scoped views
// vnd_htl_01 owns the Mercure London Heathrow (htl_lhr_01); the hotel-vendor
// portal scopes to that single hotel for the demo.
// =============================================================================
export const VENDOR_HOTEL_MAP: Record<string, string> = {
  vnd_htl_01: 'htl_lhr_01',
};
export const VENDOR_TRANSPORT_MAP: Record<string, string[]> = {
  // QRX Transport handles all transports in the seed for demo simplicity
  vnd_trn_01: ['*'],
};
// =============================================================================
// Mock users
// =============================================================================
const MOCK_USERS: User[] = [
  { id: 'usr_ops_01',     email: 'ops.controller@qatarairways.example', full_name: 'Sara Al-Mansouri',  role: 'OPS_CONTROLLER',       tenant_id: 'qr', vendor_id: null,         crew_id: null },
  { id: 'usr_stn_01',     email: 'station.lhr@qatarairways.example',     full_name: 'James Walker',      role: 'STATION_MANAGER',      tenant_id: 'qr', vendor_id: null,         crew_id: null },
  { id: 'usr_htl_01',     email: 'reservations@marriott-doh.example',    full_name: 'Maria Reyes',       role: 'HOTEL_VENDOR_USER',    tenant_id: 'qr', vendor_id: 'vnd_htl_01', crew_id: null },
  { id: 'usr_trn_01',     email: 'dispatch@qrx-transport.example',       full_name: 'Daniel Petrov',     role: 'TRANSPORT_VENDOR_USER',tenant_id: 'qr', vendor_id: 'vnd_trn_01', crew_id: null },
  { id: 'usr_crew_42',    email: 'crew42@qatarairways.example',          full_name: 'Priya Sharma',      role: 'CREW',                  tenant_id: 'qr', vendor_id: null,         crew_id: 'crw_0042' },
  { id: 'usr_fin_01',     email: 'finance@qatarairways.example',         full_name: "Robert O'Brien",    role: 'FINANCE',               tenant_id: 'qr', vendor_id: null,         crew_id: null },
  { id: 'usr_sec_01',     email: 'security@qatarairways.example',        full_name: 'Andreas Mueller',   role: 'SECURITY_REVIEWER',     tenant_id: 'qr', vendor_id: null,         crew_id: null },
  { id: 'usr_admin_01',   email: 'admin@rezolv.example',                 full_name: 'Rezolv Tenant Admin', role: 'TENANT_ADMIN',         tenant_id: 'qr', vendor_id: null,         crew_id: null },
];

export interface AppState {
  // Identity
  users: User[];
  activeUserId: string;
  // Masters
  stations: Station[];
  hotels: Hotel[];
  // Rank master (GET /api/v1/ranks/), loaded with stations/hotels
  ranks: Rank[];
  // Backend tenant settings (GET /api/v1/system-configs/), loaded with the masters
  tenantSettings: TenantSettings | null;
  crew: Crew[];
  flights: Flight[];
  trainings: Training[];
  officeDuties: OfficeDuty[];
  surfaceTravel: SurfaceTravel[];
  fxRates: FxRate[];
  // Roster
  publications: RosterPublication[];
  rosterLines: RosterLine[];
  // Projection
  projectionRuns: ProjectionRun[];
  projectionEntries: ProjectionEntry[];
  // Booking
  bookings: Booking[];
  // Events / audit / notifications
  events: RosterEvent[];
  auditLog: AuditLogEntry[];
  outbox: NotificationOutboxEntry[];
  pushNotifications: PushNotification[];
  // Chat / broadcast
  chatThreads: ChatThread[];
  chatMessages: ChatMessage[];
  broadcasts: Broadcast[];
  broadcastResponses: BroadcastResponse[];
  // Inventory alerts
  inventoryAlerts: InventoryAlert[];
  reportSchedules: import('./phase5-actions').ReportSchedule[];
  // Phase 4 slices
  invoices: Invoice[];
  feedbacks: CrewFeedback[];
  transports: Transport[];
  // Prompt 2 — RFP requests + lightweight crew assignments
  rfpRequests: RfpRequest[];
  crewAssignments: CrewAssignment[];
  // Accruals — cost accrual tracking
  accruals: AccrualEntry[];
  // Config + flags
  /** From the backend (hydrateMastersFromBackend); null until loaded or when
   * the tenant has no config. Never a hardcoded tenant constant. */
  systemConfig: SystemConfig | null;
  /** The deployment tenant's branding (hydrateBranding); null = show no airline. */
  branding: Branding | null;
  disruptionMode: boolean;
  /** Demo privacy mode (Settings, admins): crew names / emails / phones masked on screen. */
  piiMaskEnabled: boolean;
  // UTC / Local toggle for every station-bound timestamp (Topbar)
  timeMode: TimeMode;
  hydrated: boolean;
}

export interface AppActions {
  hydrate: () => void;
  resetSeed: () => void;
  setActiveUser: (userId: string) => void;
  pushAudit: (entry: AuditLogEntry) => void;
  setDisruptionMode: (on: boolean) => void;
  setPiiMaskEnabled: (on: boolean) => void;
  setTimeMode: (mode: TimeMode) => void;

  // Phase 1–3 setters (used by earlier action files)
  upsertBooking: (b: Booking) => void;
  appendBookings: (bs: Booking[]) => void;
  addOutboxEntries: (entries: NotificationOutboxEntry[]) => void;
  addChatMessage: (m: ChatMessage) => void;
  addBroadcast: (b: Broadcast) => void;
  addBroadcastResponse: (r: BroadcastResponse) => void;
  addPushNotification: (p: PushNotification) => void;
  /** Prompt 1 / Part 1d — mark a single push as read (Inbox tap). */
  markPushRead: (pushId: string, userId?: string) => void;
  /** Prompt 1 / Part 1d — mark all of a user's pushes read. */
  markAllPushRead: (userId: string) => void;

  // Phase 4 setters
  addInvoice: (i: Invoice) => void;
  updateInvoice: (i: Invoice) => void;
  addFeedback: (f: CrewFeedback) => void;
  updateFeedback: (f: CrewFeedback) => void;
  addTransport: (t: Transport) => void;
  updateTransport: (t: Transport) => void;
  markThreadRead: (threadId: string, userId: string) => void;
  // Prompt 2 — RFP + crew assignment setters
  addRfpRequest: (r: RfpRequest) => void;
  updateRfpRequest: (r: RfpRequest) => void;
  addCrewAssignment: (a: CrewAssignment) => void;
  // Accrual setters
  addAccrual: (a: AccrualEntry) => void;
  updateAccrual: (a: AccrualEntry) => void;
}

function buildInitialState(seed: SeedBundle): AppState {
  return {
    users: MOCK_USERS,
    activeUserId: MOCK_USERS[0]!.id,
    stations: seed.stations,
    hotels: seed.hotels,
    ranks: [],
    tenantSettings: null,
    crew: seed.crew,
    flights: seed.flights,
    trainings: seed.trainings,
    officeDuties: seed.officeDuties,
    surfaceTravel: seed.surfaceTravel,
    fxRates: seed.fxRates,
    publications: seed.publications,
    rosterLines: seed.rosterLines,
    projectionRuns: [],
    projectionEntries: [],
    bookings: seed.bookings,
    events: [],
    auditLog: [],
    outbox: seed.outbox ?? [],
    pushNotifications: seed.pushNotifications ?? [],
    chatThreads: seed.chatThreads,
    chatMessages: seed.chatMessages,
    broadcasts: seed.broadcasts,
    broadcastResponses: seed.broadcastResponses,
    inventoryAlerts: seed.inventoryAlerts,
    reportSchedules: [],
    invoices: seed.invoices ?? [],
    feedbacks: seed.feedbacks ?? [],
    transports: seed.transports ?? [],
    rfpRequests: [],
    crewAssignments: [],
    accruals: seed.accruals ?? [],
    systemConfig: null,
    branding: null,
    disruptionMode: false,
    piiMaskEnabled: true,
    timeMode: 'LOCAL',
    hydrated: false,
  };
}

const FRESH_SEED = loadSeed();
const INITIAL: AppState = buildInitialState(FRESH_SEED);

export const useAppStore = create<AppState & AppActions>()(
  subscribeWithSelector((set) => ({
    ...INITIAL,

    hydrate: () => {
      // Load only allowlisted fields from localStorage (no PII, no systemConfig)
      const persisted = loadFromLocalStorage<Partial<AppState>>();
      if (persisted) {
        // Build partial update from allowlisted fields only
        const safeUpdate: Partial<AppState> = {};
        for (const key of PERSIST_ALLOWLIST) {
          if (key in persisted && persisted[key as keyof AppState] !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (safeUpdate as any)[key] = persisted[key as keyof AppState];
          }
        }
        // Stored value is untrusted: anything but a known mode keeps the default.
        if (safeUpdate.timeMode !== undefined && safeUpdate.timeMode !== 'LOCAL' && safeUpdate.timeMode !== 'UTC') {
          delete safeUpdate.timeMode;
        }
        // systemConfig and branding come only from the backend, never from
        // localStorage.
        set({
          ...safeUpdate,
          hydrated: true,
        });
      } else {
        set({ hydrated: true });
      }
    },

    resetSeed: () => {
      clearLocalStorage();
      const fresh = buildInitialState(loadSeed());
      // Seed reset (tests) keeps the backend-loaded config and branding.
      set((s) => ({ ...fresh, systemConfig: s.systemConfig, branding: s.branding, hydrated: true }));
    },

    setActiveUser: (userId) => set({ activeUserId: userId }),
    pushAudit: (entry) => set((s) => ({ auditLog: [...s.auditLog, entry] })),
    setDisruptionMode: (on) => set({ disruptionMode: on }),
    setPiiMaskEnabled: (on) => set({ piiMaskEnabled: on }),
    setTimeMode: (mode) => set({ timeMode: mode }),


    // Phase 1–3 setters
    upsertBooking: (b) =>
      set((s) => {
        const idx = s.bookings.findIndex((x) => x.id === b.id);
        if (idx === -1) return { bookings: [...s.bookings, b] };
        const next = [...s.bookings];
        next[idx] = b;
        return { bookings: next };
      }),
    appendBookings: (bs) => set((s) => ({ bookings: [...s.bookings, ...bs] })),
    addOutboxEntries: (entries) => set((s) => ({ outbox: [...s.outbox, ...entries] })),
    addChatMessage: (m) => set((s) => ({ chatMessages: [...s.chatMessages, m] })),
    addBroadcast: (b) => set((s) => ({ broadcasts: [...s.broadcasts, b] })),
    addBroadcastResponse: (r) => set((s) => ({ broadcastResponses: [...s.broadcastResponses, r] })),
    addPushNotification: (p) => set((s) => ({ pushNotifications: [...s.pushNotifications, p] })),
    markPushRead: (pushId, userId) =>
      set((s) => ({
        pushNotifications: s.pushNotifications.map((p) =>
          p.id === pushId && (!userId || p.recipient_user_id === userId)
            ? { ...p, read: true }
            : p,
        ),
      })),
    markAllPushRead: (userId) =>
      set((s) => ({
        pushNotifications: s.pushNotifications.map((p) =>
          p.recipient_user_id === userId ? { ...p, read: true } : p,
        ),
      })),

    // Phase 4 setters
    addInvoice: (i) => set((s) => ({ invoices: [...s.invoices, i] })),
    updateInvoice: (i) =>
      set((s) => ({ invoices: s.invoices.map((x) => (x.id === i.id ? i : x)) })),
    addFeedback: (f) => set((s) => ({ feedbacks: [...s.feedbacks, f] })),
    updateFeedback: (f) =>
      set((s) => ({ feedbacks: s.feedbacks.map((x) => (x.id === f.id ? f : x)) })),
    addTransport: (t) => set((s) => ({ transports: [...s.transports, t] })),
    updateTransport: (t) =>
      set((s) => ({ transports: s.transports.map((x) => (x.id === t.id ? t : x)) })),
    markThreadRead: (threadId, userId) =>
      set((s) => ({
        chatThreads: s.chatThreads.map((t) =>
          t.id === threadId
            ? { ...t, unread_count_by_user: { ...t.unread_count_by_user, [userId]: 0 } }
            : t,
        ),
        chatMessages: s.chatMessages.map((m) =>
          m.thread_id === threadId && !m.read_by_user_ids.includes(userId)
            ? { ...m, read_by_user_ids: [...m.read_by_user_ids, userId] }
            : m,
        ),
      })),
    // Prompt 2 — RFP + crew assignment setters
    addRfpRequest: (r) => set((s) => ({ rfpRequests: [...s.rfpRequests, r] })),
    updateRfpRequest: (r) =>
      set((s) => ({ rfpRequests: s.rfpRequests.map((x) => (x.id === r.id ? r : x)) })),
    addCrewAssignment: (a) => set((s) => ({ crewAssignments: [...s.crewAssignments, a] })),
    // Accrual setters
    addAccrual: (a) => set((s) => ({ accruals: [...s.accruals, a] })),
    updateAccrual: (a) =>
      set((s) => ({ accruals: s.accruals.map((x) => (x.id === a.id ? a : x)) })),
  })),
);

// Persistence write-through — only allowlisted fields are saved (see PERSIST_ALLOWLIST)
if (typeof window !== 'undefined') {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  useAppStore.subscribe((state) => {
    if (!state.hydrated) return;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      // saveToLocalStorage internally filters to PERSIST_ALLOWLIST — no PII persisted
      saveToLocalStorage(state as unknown as Record<string, unknown>);
    }, 150);
  });
}

// Selectors
export function activeUserSelector(s: AppState): User {
  const u = s.users.find((x) => x.id === s.activeUserId);
  if (!u) throw new Error(`Active user not found: ${s.activeUserId}`);
  return u;
}

// Screens the backend restricts to these roles (403 for anyone else). Applied
// on top of each role's nav below, so a role never gains a screen here.
const RESTRICTED_SCREENS: Record<string, UserRole[]> = {
  'audit-log': ['SUPER_ADMIN', 'SECURITY_REVIEWER'],
  outbox: ['OPS_CONTROLLER', 'STATION_MANAGER', 'TENANT_ADMIN', 'SUPER_ADMIN'],
  broadcast: ['OPS_CONTROLLER', 'STATION_MANAGER', 'TENANT_ADMIN', 'SUPER_ADMIN'],
};

function allowedFor(role: UserRole) {
  return (key: string) => !RESTRICTED_SCREENS[key] || RESTRICTED_SCREENS[key]!.includes(role);
}

export function rolesForRoute(role: UserRole): { primary: string[]; secondary: string[] } {
  const nav = navForRole(role);
  const allowed = allowedFor(role);
  return { primary: nav.primary.filter(allowed), secondary: nav.secondary.filter(allowed) };
}

/** Whether `role` may open the screen `key` (the sidebar and RoleRoute agree). */
export function canAccessRoute(role: UserRole, key: string): boolean {
  const { primary, secondary } = rolesForRoute(role);
  return primary.includes(key) || secondary.includes(key);
}

function navForRole(role: UserRole): { primary: string[]; secondary: string[] } {
  const ALL_PRIMARY = ['roster','projection','booking','hotel-approval','hotels','stations','crew-master','crew','dashboard','console','reports','accruals','invoices','analytics'];
  const ALL_SECONDARY = ['settings','audit-log','outbox','events','communication','broadcast','inventory-alerts','peak-dates','transport-view','feedback','nearest-crew','api-inventory','risma'];

  if (role === 'OPS_CONTROLLER' || role === 'TENANT_ADMIN' || role === 'SUPER_ADMIN') {
    return { primary: ALL_PRIMARY, secondary: ALL_SECONDARY };
  }
  if (role === 'HOTEL_VENDOR_USER') {
    return { primary: ['hotel-approval'], secondary: ['communication','feedback'] };
  }
  if (role === 'TRANSPORT_VENDOR_USER') {
    return { primary: [], secondary: ['transport-view','communication'] };
  }
  if (role === 'CREW') {
    return { primary: ['crew'], secondary: ['communication','broadcast'] };
  }
  if (role === 'FINANCE') {
    return { primary: ['accruals','invoices','reports','analytics'], secondary: ['audit-log','outbox'] };
  }
  if (role === 'SECURITY_REVIEWER') {
    return { primary: ['hotel-approval','hotels','stations','crew-master','reports'], secondary: ['audit-log'] };
  }
  if (role === 'STATION_MANAGER') {
    return { primary: ['booking','hotel-approval','hotels','stations','crew-master','dashboard'], secondary: ['events','communication','inventory-alerts','feedback','nearest-crew'] };
  }
  return { primary: [], secondary: [] };
}

/** Hotel(s) the given vendor user can act on, scoped via VENDOR_HOTEL_MAP. */
export function hotelsForVendor(vendorId: string | null): string[] {
  if (!vendorId) return [];
  const direct = VENDOR_HOTEL_MAP[vendorId];
  return direct ? [direct] : [];
}
